import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { post } from '@/utils/apiClient';
import { logger } from "../../../utils/logger";
import { DEFAULT_FOLDERS } from '../constants';

function VaultSaveModal({ isOpen, onClose, message, messages, selectedMessages = [] }) {
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [folder, setFolder] = useState('General');
  const [showCustomFolder, setShowCustomFolder] = useState(false);
  const [customFolderName, setCustomFolderName] = useState('');
  const [contextMessages, setContextMessages] = useState([]);
  const [folders, setFolders] = useState([...DEFAULT_FOLDERS]);
  
  useEffect(() => {
    if (isOpen && message) {
      if (message.selectedText) {
        setText(message.selectedText);
      } else {
        setText(message.text || '');
      }
      
      if (selectedMessages && selectedMessages.length > 0) {
        setContextMessages(
          selectedMessages.map(idx => ({
            role: messages[idx].sender,
            content: messages[idx].text
          }))
        );
      } else {
        setContextMessages(
          messages
            .slice(Math.max(0, messages.indexOf(message) - 2), messages.indexOf(message) + 1)
            .map(msg => ({
              role: msg.sender,
              content: msg.text
            }))
        );
      }
    }
  }, [isOpen, message, messages, selectedMessages]);
  
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };
  
  const handleSave = async () => {
    try {
      const finalFolder = showCustomFolder ? customFolderName.trim() : folder;
      
      if (showCustomFolder && !finalFolder) {
        toast.error("Please enter a folder name");
        return;
      }
      
      await post('/api/vault/save', {
        text: text,
        title: title.trim() || null,
        tags: tags.length > 0 ? tags : null,
        folder: finalFolder,
        surrounding_turns: contextMessages
      });
      
      toast.success("Saved to Library!");
      if (showCustomFolder && !folders.includes(finalFolder)) {
        setFolders([...folders, finalFolder]);
      }
    } catch (err) {
      logger.error(err);
      toast.error("Error saving idea.");
    } finally {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Save to Library</h2>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {selectedMessages && selectedMessages.length > 0 
                ? `Selected Messages (${selectedMessages.length})` 
                : "Message"}
            </label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600 min-h-[100px] max-h-[150px] overflow-auto">
              {text}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
              placeholder="Add a title..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Tags</label>
            <div className="flex items-center">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                className="flex-1 p-2 border dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                placeholder="Add tags..."
              />
              <button
                onClick={handleAddTag}
                className="ml-2 bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded-full text-sm flex items-center"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={showCustomFolder ? 'custom' : folder}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setShowCustomFolder(true);
                } else {
                  setShowCustomFolder(false);
                  setFolder(e.target.value);
                }
              }}
              className="w-full p-2 border dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
            >
              {folders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
              <option value="custom">+ Create New Folder</option>
            </select>
            
            {showCustomFolder && (
              <input
                type="text"
                value={customFolderName}
                onChange={(e) => setCustomFolderName(e.target.value)}
                placeholder="Enter new folder name..."
                className="w-full mt-2 p-2 border dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
              />
            )}
          </div>
        </div>
        
        <div className="p-4 border-t dark:border-gray-700 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Save to Library
          </button>
        </div>
      </div>
    </div>
  );
}

export default VaultSaveModal;
